const STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "by",
  "for",
  "from",
  "how",
  "in",
  "into",
  "is",
  "it",
  "of",
  "on",
  "or",
  "that",
  "the",
  "their",
  "this",
  "to",
  "using",
  "what",
  "when",
  "where",
  "which",
  "who",
  "will",
  "with",
]);

const SECTION_ALIASES = {
  objective: ["objective", "learning objective", "objectives"],
  assessment: ["assessment", "lesson assessment"],
  opening: ["opening", "warm-up", "warm up", "hook"],
};

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const buildContentWords = (value) =>
  Array.from(
    new Set(
      normalizeText(value)
        .split(" ")
        .filter((word) => word.length >= 4 && !STOP_WORDS.has(word))
    )
  );

const overlapRatio = (sourceText, targetText) => {
  const sourceWords = buildContentWords(sourceText);
  const targetWords = new Set(buildContentWords(targetText));
  if (sourceWords.length === 0) return 1;
  const matches = sourceWords.filter((word) => targetWords.has(word)).length;
  return matches / sourceWords.length;
};

const findSection = (sections = [], aliases = []) => {
  const normalizedAliases = aliases.map((alias) => normalizeText(alias));
  return (
    sections.find((section) =>
      normalizedAliases.some((alias) => normalizeText(section.title).includes(alias))
    ) || null
  );
};

const countTeacherAnchorSignals = (criteria = {}) => {
  const teacherEditedState =
    criteria && typeof criteria.teacherEditedState === "object"
      ? criteria.teacherEditedState
      : {};
  let count = 0;

  if (teacherEditedState.blueprint || criteria?.lessonBlueprint) count += 1;
  if (teacherEditedState.warmUp || criteria?.warmUpQuestion) count += 1;
  if (
    teacherEditedState.essentials ||
    (Array.isArray(criteria?.essentialQuestions) && criteria.essentialQuestions.length > 0)
  ) {
    count += 1;
  }

  return count;
};

const parseMinutesFromMatch = (match) => {
  const start = Number(match?.[1] || 0);
  const end = Number(match?.[2] || match?.[3] || 0);
  if (!Number.isFinite(start) || start <= 0) return 0;
  if (Number.isFinite(end) && end > 0) {
    return Math.max(start, end);
  }
  return start;
};

export const parseClassPeriodMinutes = (value) => {
  const numeric = Number.parseInt(String(value || "").replace(/[^\d]/g, ""), 10);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

export const extractMarkdownSections = (content = "") => {
  const lines = String(content || "").split("\n");
  const sections = [];
  let current = null;

  lines.forEach((line) => {
    const headingMatch = line.match(/^##+\s+(.+?)\s*$/);
    if (headingMatch) {
      if (current) {
        current.content = current.content.trim();
        sections.push(current);
      }
      current = {
        title: headingMatch[1].trim(),
        content: "",
      };
      return;
    }

    if (!current) return;
    current.content += `${line}\n`;
  });

  if (current) {
    current.content = current.content.trim();
    sections.push(current);
  }

  return sections;
};

const sumSectionTimes = (sections = []) =>
  sections.reduce((total, section) => {
    const explicitTime = section.content.match(
      /(?:^|\n)\s*(?:-|•)?\s*Time(?:\s+allocation)?\s*:\s*(\d+)(?:\s*-\s*(\d+)|\s*to\s*(\d+))?\s*minutes?/i
    );
    if (explicitTime) {
      return total + parseMinutesFromMatch(explicitTime);
    }

    const headingTime = section.title.match(
      /\((\d+)(?:\s*-\s*(\d+)|\s*to\s*(\d+))?\s*minutes?\)/i
    );
    if (headingTime) {
      return total + parseMinutesFromMatch(headingTime);
    }

    return total;
  }, 0);

const countMatchedResourceUrls = (content = "", relevantResources = []) => {
  const matchedUrls = new Set();
  relevantResources.forEach((resource) => {
    const url = String(resource?.url || "").trim();
    if (url && content.includes(url)) {
      matchedUrls.add(url);
    }
  });
  return matchedUrls.size;
};

const hasAnchorMatch = (anchorText, sectionText, minimum = 0.6) =>
  !anchorText || overlapRatio(anchorText, sectionText) >= minimum;

export const evaluateLessonPlanCandidate = ({
  content = "",
  criteria = {},
  relevantResources = [],
} = {}) => {
  const sections = extractMarkdownSections(content);
  const objectiveSection = findSection(sections, SECTION_ALIASES.objective);
  const assessmentSection = findSection(sections, SECTION_ALIASES.assessment);
  const openingSection = findSection(sections, SECTION_ALIASES.opening);
  const classPeriodMinutes = parseClassPeriodMinutes(criteria?.classPeriodLength);
  const totalTimedMinutes = sumSectionTimes(sections);
  const teacherEditedState =
    criteria && typeof criteria.teacherEditedState === "object"
      ? criteria.teacherEditedState
      : {};
  const hardFailures = [];

  if (!objectiveSection?.content) {
    hardFailures.push({
      id: "missing_objective",
      message: "Missing objective section.",
    });
  }

  if (!assessmentSection?.content) {
    hardFailures.push({
      id: "missing_assessment",
      message: "Missing assessment section.",
    });
  }

  if (
    classPeriodMinutes &&
    totalTimedMinutes > 0 &&
    totalTimedMinutes > classPeriodMinutes
  ) {
    hardFailures.push({
      id: "period_scope",
      message: `Planned time (${totalTimedMinutes} minutes) exceeds class period (${classPeriodMinutes} minutes).`,
    });
  }

  const blueprint = criteria?.lessonBlueprint || {};
  if (
    teacherEditedState.blueprint &&
    blueprint.objective &&
    !hasAnchorMatch(blueprint.objective, objectiveSection?.content || "", 0.6)
  ) {
    hardFailures.push({
      id: "teacher_blueprint_objective",
      message: "Objective does not preserve the teacher-edited blueprint objective.",
    });
  }

  if (
    teacherEditedState.blueprint &&
    blueprint.assessment &&
    !hasAnchorMatch(blueprint.assessment, assessmentSection?.content || "", 0.5)
  ) {
    hardFailures.push({
      id: "teacher_blueprint_assessment",
      message: "Assessment does not preserve the teacher-edited blueprint assessment.",
    });
  }

  if (
    teacherEditedState.warmUp &&
    criteria?.warmUpQuestion &&
    !hasAnchorMatch(criteria.warmUpQuestion, openingSection?.content || "", 0.45)
  ) {
    hardFailures.push({
      id: "teacher_warmup",
      message: "Opening does not preserve the teacher-edited warm-up question.",
    });
  }

  const standards = Array.isArray(criteria?.standards)
    ? criteria.standards.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  if (
    standards.length > 0 &&
    !standards.some((standard) => content.includes(standard))
  ) {
    hardFailures.push({
      id: "standards_missing",
      message: "Plan does not include the provided standard codes.",
    });
  }

  const matchedResourceCount = countMatchedResourceUrls(content, relevantResources);
  const minimumResourceMatches =
    relevantResources.length >= 8 ? 3 : relevantResources.length > 0 ? 1 : 0;
  if (minimumResourceMatches > 0 && matchedResourceCount < minimumResourceMatches) {
    hardFailures.push({
      id: "resource_usage",
      message: `Plan references only ${matchedResourceCount} provided resource URL(s).`,
    });
  }

  if (
    objectiveSection?.content &&
    assessmentSection?.content &&
    overlapRatio(objectiveSection.content, assessmentSection.content) < 0.2
  ) {
    hardFailures.push({
      id: "objective_assessment_mismatch",
      message: "Assessment appears disconnected from the objective.",
    });
  }

  const objectiveAssessmentAlignment = overlapRatio(
    objectiveSection?.content || "",
    assessmentSection?.content || ""
  );
  const timeDelta =
    classPeriodMinutes && totalTimedMinutes > 0
      ? Math.abs(classPeriodMinutes - totalTimedMinutes)
      : null;
  const deterministicScore =
    Math.min(matchedResourceCount, 6) * 2 +
    (objectiveSection?.content ? 3 : 0) +
    (assessmentSection?.content ? 3 : 0) +
    (openingSection?.content ? 2 : 0) +
    (objectiveAssessmentAlignment >= 0.35
      ? 6
      : objectiveAssessmentAlignment >= 0.25
        ? 4
        : objectiveAssessmentAlignment >= 0.2
          ? 2
          : 0) +
    (/\?/.test(openingSection?.content || "") ? 2 : 0) +
    (timeDelta === null
      ? 2
      : Math.max(0, 10 - Math.min(10, timeDelta)));

  return {
    pass: hardFailures.length === 0,
    hardFailures,
    totalTimedMinutes,
    matchedResourceCount,
    objectiveAssessmentAlignment,
    timeDelta,
    deterministicScore,
    sections: {
      objective: objectiveSection?.content || "",
      assessment: assessmentSection?.content || "",
      opening: openingSection?.content || "",
    },
  };
};

export const determineLessonPlanChallengeStrategy = ({
  userInput = "",
  criteria = {},
  relevantResources = [],
  metadata = {},
} = {}) => {
  if (metadata?.forceHiddenChallenge === true) {
    return {
      strategy: "multi_draft",
      riskScore: 999,
      reasons: ["forced_hidden_challenge"],
    };
  }

  const contentWordCount = buildContentWords(userInput).length;
  const classPeriodMinutes = parseClassPeriodMinutes(criteria?.classPeriodLength);
  const standardsCount = Array.isArray(criteria?.standards)
    ? criteria.standards.filter(Boolean).length
    : 0;
  const gradeCount = Array.isArray(criteria?.grades)
    ? criteria.grades.filter(Boolean).length
    : 0;
  const resourceCount = Array.isArray(relevantResources)
    ? relevantResources.length
    : 0;
  const uploadCount =
    (Array.isArray(criteria?.localFiles) ? criteria.localFiles.length : 0) +
    (Array.isArray(criteria?.driveFiles) ? criteria.driveFiles.length : 0);
  const teacherAnchorCount = countTeacherAnchorSignals(criteria);
  const reasons = [];
  let riskScore = 0;

  if (criteria?.useAssignmentData) {
    riskScore += 3;
    reasons.push("assignment_data");
  }
  if (criteria?.previousLessonPlanProvided) {
    riskScore += 1;
    reasons.push("follow_up_plan");
  }
  if (contentWordCount >= 18) {
    riskScore += 2;
    reasons.push("broad_prompt");
  } else if (contentWordCount >= 10) {
    riskScore += 1;
    reasons.push("multi_constraint_prompt");
  }
  if (resourceCount >= 8) {
    riskScore += 2;
    reasons.push("resource_heavy");
  } else if (resourceCount >= 4) {
    riskScore += 1;
    reasons.push("resource_rich");
  }
  if (standardsCount >= 3) {
    riskScore += 1;
    reasons.push("many_standards");
  }
  if (gradeCount >= 3) {
    riskScore += 1;
    reasons.push("many_grades");
  }
  if (!classPeriodMinutes) {
    riskScore += 1;
    reasons.push("no_period_bound");
  }
  if (teacherAnchorCount === 0) {
    riskScore += 2;
    reasons.push("no_teacher_anchors");
  } else if (teacherAnchorCount === 1) {
    riskScore += 1;
    reasons.push("limited_teacher_anchors");
  }
  if (uploadCount > 0) {
    riskScore += 1;
    reasons.push("uploaded_materials");
  }

  return {
    strategy:
      riskScore >= 4 ? "multi_draft" : "primary_only_with_recovery",
    riskScore,
    reasons,
    signals: {
      contentWordCount,
      standardsCount,
      gradeCount,
      resourceCount,
      uploadCount,
      teacherAnchorCount,
      classPeriodMinutes,
    },
  };
};

export const selectDeterministicLessonPlanWinner = ({
  primaryEvaluation = null,
  challengerEvaluation = null,
} = {}) => {
  if (!primaryEvaluation?.pass || !challengerEvaluation?.pass) {
    return null;
  }

  const primaryScore = Number(primaryEvaluation?.deterministicScore || 0);
  const challengerScore = Number(challengerEvaluation?.deterministicScore || 0);
  const scoreDelta = Math.abs(primaryScore - challengerScore);

  if (scoreDelta < 4) {
    return null;
  }

  return {
    winner: challengerScore > primaryScore ? "B" : "A",
    reason: "deterministic_score_margin",
    usedJudge: false,
    primaryScore,
    challengerScore,
    scoreDelta,
  };
};

export const buildLessonPlanComparePrompt = ({
  userInput = "",
  criteria = {},
  primaryContent = "",
  challengerContent = "",
  primaryEvaluation = null,
  challengerEvaluation = null,
} = {}) => {
  const classPeriodMinutes = parseClassPeriodMinutes(criteria?.classPeriodLength);
  const standards = Array.isArray(criteria?.standards)
    ? criteria.standards.filter(Boolean).join(", ")
    : "";
  const grades = Array.isArray(criteria?.grades)
    ? criteria.grades.filter(Boolean).join(", ")
    : "";

  return `You are choosing the better single-class-period lesson plan draft for a teacher.

Return JSON only in this schema:
{"winner":"A","rationale":"...","scores":{"A":{"periodRealism":0,"objectiveAssessmentCoherence":0,"blueprintAlignment":0,"warmUpFreshness":0,"resourceIntegration":0,"perspectiveAccuracy":0,"classroomUsability":0},"B":{"periodRealism":0,"objectiveAssessmentCoherence":0,"blueprintAlignment":0,"warmUpFreshness":0,"resourceIntegration":0,"perspectiveAccuracy":0,"classroomUsability":0}},"criticalNotes":["..."]}

Scoring rules:
- Use integers from 0 to 5 for every score.
- Prefer realistic one-period scope over ambitious coverage.
- Prefer one strong objective and one clear assessment path.
- Prefer an opening that feels like genuine inquiry, not a restatement of the topic.
- Treat teacher-authored constraints as binding.
- Prefer the better full draft; do not propose a blend.
- If the drafts are effectively tied, set winner to "A".

Teacher goal:
${String(userInput || "").trim()}

Context:
- Class period length: ${classPeriodMinutes || "not specified"} minutes
- Grades: ${grades || "not specified"}
- Standards: ${standards || "not specified"}
- Warm-up selected: ${criteria?.warmUpQuestion ? "yes" : "no"}
- Blueprint provided: ${criteria?.lessonBlueprint ? "yes" : "no"}

Deterministic check summary:
- Draft A hard failures: ${
    primaryEvaluation?.hardFailures?.map((item) => item.message).join(" | ") || "none"
  }
- Draft B hard failures: ${
    challengerEvaluation?.hardFailures?.map((item) => item.message).join(" | ") || "none"
  }

Draft A:
${primaryContent}

Draft B:
${challengerContent}`;
};

export const selectLessonPlanWinner = ({
  primaryEvaluation = null,
  challengerEvaluation = null,
  judgeResult = null,
} = {}) => {
  const primaryPass = Boolean(primaryEvaluation?.pass);
  const challengerPass = Boolean(challengerEvaluation?.pass);

  if (primaryPass && !challengerPass) {
    return {
      winner: "A",
      reason: "challenger_failed_hard_checks",
      usedJudge: false,
    };
  }

  if (challengerPass && !primaryPass) {
    return {
      winner: "B",
      reason: "primary_failed_hard_checks",
      usedJudge: false,
    };
  }

  if (!primaryPass && !challengerPass) {
    return {
      winner: "A",
      reason: "both_failed_hard_checks",
      usedJudge: false,
    };
  }

  const normalizedWinner = String(judgeResult?.winner || "").trim().toUpperCase();
  if (normalizedWinner === "B") {
    return {
      winner: "B",
      reason: "judge_selected_challenger",
      usedJudge: true,
    };
  }

  return {
    winner: "A",
    reason:
      normalizedWinner === "A" ? "judge_selected_primary" : "judge_tie_or_failure",
    usedJudge: Boolean(judgeResult),
  };
};
